import fs from 'fs/promises'
import path from 'path'
import { glob } from 'glob'
import mime from 'mime-types'
import type { FileToUpload } from './types.js'

/**
 * Resolve files from glob patterns or file paths
 */
export async function resolveFiles(patterns: (string | FileToUpload)[]): Promise<FileToUpload[]> {
  const resolved: FileToUpload[] = []

  for (const pattern of patterns) {
    if (typeof pattern === 'string') {
      // Handle glob patterns
      const matches = await glob(pattern, { nodir: true, absolute: true })

      for (const filePath of matches) {
        const contentType = mime.lookup(filePath) || 'application/octet-stream'
        resolved.push({
          path: filePath,
          destination: path.basename(filePath),
          contentType
        })
      }
    } else {
      // Already a FileToUpload object
      const contentType = pattern.contentType || mime.lookup(pattern.path) || 'application/octet-stream'
      resolved.push({
        ...pattern,
        contentType
      })
    }
  }

  return resolved
}

/**
 * Get file size in bytes
 */
export async function getFileSize(filePath: string): Promise<number> {
  const stats = await fs.stat(filePath)
  return stats.size
}

/**
 * Ensure directory exists
 */
export async function ensureDir(dirPath: string): Promise<void> {
  try {
    await fs.access(dirPath)
  } catch {
    await fs.mkdir(dirPath, { recursive: true })
  }
}

/**
 * Format bytes to human readable string
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

/**
 * Format duration to human readable string
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`
  const minutes = Math.floor(ms / 60000)
  const seconds = ((ms % 60000) / 1000).toFixed(0)
  return `${minutes}m ${seconds}s`
}

/**
 * Sanitize path for storage (remove leading slashes, normalize)
 */
export function sanitizePath(filePath: string): string {
  return filePath.replace(/^\/+/, '').replace(/\\/g, '/')
}

/**
 * Get ISO timestamp string
 */
export function getTimestamp(): string {
  return new Date().toISOString()
}
